import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import { Radio, RadioGroup, Text } from '@revelio/ui';

import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { PreferenceScope, UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import {
  resolveWorkforceSegment,
  type WorkforceSegment,
} from '../utils/workforce-segment';
import layoutStyles from './contingent-workers-editor.module.css';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.ContingentWorkers,
  label: 'Contingent Workers',
};

export default function ContingentWorkersEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const [segment, setSegment] = useState<WorkforceSegment>(() =>
    resolveWorkforceSegment(preferences[UserPreferenceKey.ContingentWorkers])
  );

  useEffect(() => {
    setSegment(
      resolveWorkforceSegment(preferences[UserPreferenceKey.ContingentWorkers])
    );
  }, [preferences]);

  const onUpdate = useCallback(
    (value: string) => {
      const nextSegment = resolveWorkforceSegment(value);

      onPreferenceUpdate?.(UserPreferenceKey.ContingentWorkers, nextSegment);
      setSegment(nextSegment);
      // Single GLOBAL choice: always write the GLOBAL record regardless of
      // which page's modal made the change (same pattern as TaxonomiesEditor).
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.ContingentWorkers,
        nextSegment
      );
    },
    [onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Workforce Segment"
        indicateDiff={diff?.includes(meta.id) ?? false}
      />
      <RadioGroup
        value={segment}
        onChange={onUpdate}
        className={styles.radioGroup}
      >
        <div className={layoutStyles.choiceList}>
          <div className={layoutStyles.choice}>
            <Radio value="full" className={styles.appearanceRadio}>
              Full Workforce
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Full Workforce includes all workers: employees, contractors,
              consultants, and other contingent workers who may not appear in
              official company reports.
            </Text>
          </div>
          <div className={layoutStyles.choice}>
            <Radio value="excludeContingent" className={styles.appearanceRadio}>
              Exclude Contingent
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Exclude Contingent shows only traditional employees, excluding
              contractors, freelancers, and temporary staff.
            </Text>
          </div>
          <div className={layoutStyles.choice}>
            <Radio value="contingentOnly" className={styles.appearanceRadio}>
              Contingent Only
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Contingent Only shows only contingent workers — contractors,
              freelancers, consultants, and temporary staff — excluding
              traditional employees.
            </Text>
          </div>
        </div>
      </RadioGroup>
    </>
  );
}
